import React, { useCallback, useEffect, useState } from 'react';

import BulkPermissionUpdate from '@/components/blocks/rbac/bulk-permission-update';
import { SuperAdminGuard } from '@/components/guards/permission-guards';
import { API_ENDPOINTS } from '@/config/api-endpoints';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { Role, User } from '@/types/sheets';

interface BulkPermissionUpdateSheetProps {
  initialUsers?: User[];
  initialRoles?: Role[];
}

const BulkPermissionUpdateSheet: React.FC<BulkPermissionUpdateSheetProps> = ({
  initialUsers = [],
  initialRoles = [],
}) => {
  const { hideSheet, state } = useGlobalState();
  const { showError } = useToast();
  const api = useEnhancedApi();

  // All content must be wrapped in SuperAdminGuard
  return (
    <SuperAdminGuard>
      <BulkPermissionUpdateContent
        initialUsers={initialUsers}
        initialRoles={initialRoles}
        hideSheet={hideSheet}
        state={state}
        showError={showError}
        api={api}
      />
    </SuperAdminGuard>
  );
};

// Extract the component logic to avoid multiple guard wrapping
const BulkPermissionUpdateContent: React.FC<{
  initialUsers: User[];
  initialRoles: Role[];
  hideSheet: (id: string) => void;
  state: any;
  showError: (message: string) => void;
  api: any;
}> = ({ initialUsers, initialRoles, hideSheet, state, showError, api }) => {
  const [users, setUsers] = useState<User[]>(initialUsers);
  const [roles, setRoles] = useState<Role[]>(initialRoles);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch users and roles data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use initial data if provided to avoid unnecessary API calls
      if (initialUsers.length > 0 && initialRoles.length > 0) {
        setUsers(initialUsers);
        setRoles(initialRoles);
        setLoading(false);
        return;
      }

      const [usersResponse, rolesResponse] = await Promise.all([
        api.get(API_ENDPOINTS.USERS.LIST),
        api.get(API_ENDPOINTS.RBAC.GET_ROLES),
      ]);

      // Process users response
      if (usersResponse.success && usersResponse.data?.users) {
        const transformedUsers = usersResponse.data.users.map((user: any) => ({
          ...user,
          permissions:
            user.permissions?.reduce(
              (acc: any, perm: any) => {
                acc[perm.resource] = perm.actions;
                return acc;
              },
              {} as { [key: string]: string[] }
            ) || {},
        }));
        setUsers(transformedUsers);
      } else {
        throw new Error('Failed to fetch users data');
      }

      // Process roles response
      if (rolesResponse.success && rolesResponse.data?.roles) {
        // Filter for active roles only
        const activeRoles = rolesResponse.data.roles.filter(
          (role: Role) => role.isActive !== false
        );
        setRoles(activeRoles);
      } else {
        throw new Error('Failed to fetch roles data');
      }
    } catch (error) {
      const errorMessage =
        'Failed to load users and roles data. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [api, showError]); // Removed initialUsers, initialRoles to prevent infinite loop

  useEffect(() => {
    fetchData();
  }, []); // Only run once on mount

  const handleSuccess = useCallback(async () => {
    try {
      // Get refresh callback from sheet data
      const currentSheet = state.sheets.find(
        (sheet: any) => sheet.id === 'bulk-permission-update'
      );
      const onRefresh = currentSheet?.data?.onRefresh;

      // Close the sheet
      hideSheet('bulk-permission-update');

      // Call refresh callback if available
      if (onRefresh && typeof onRefresh === 'function') {
        try {
          await onRefresh();
        } catch (error) {
          // If refresh fails, show an error but don't re-open the sheet
          showError(
            'Data refreshed with some issues. Please refresh manually if needed.'
          );
        }
      }
    } catch (error) {
      // Force close sheet even if there's an error
      hideSheet('bulk-permission-update');
    }
  }, [hideSheet, state.sheets, showError]);

  const handleCancel = useCallback(() => {
    hideSheet('bulk-permission-update');
  }, [hideSheet]);

  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Loading state
  if (loading) {
    return (
      <div className='flex items-center justify-center p-12'>
        <div className='text-center space-y-4'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-brand-60 mx-auto'></div>
          <div>
            <p className='text-lg font-medium text-gray-90'>
              Loading Users & Roles
            </p>
            <p className='text-sm text-gray-60 mt-1'>
              Preparing bulk permission update interface...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='flex items-center justify-center p-12'>
        <div className='text-center space-y-4'>
          <div className='h-12 w-12 rounded-full bg-destructive-10 flex items-center justify-center mx-auto'>
            <svg
              className='h-6 w-6 text-destructive-60'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <div>
            <p className='text-lg font-medium text-gray-90'>
              Unable to Load Data
            </p>
            <p className='text-sm text-gray-60 mt-1'>{error}</p>
          </div>
          <button
            onClick={handleRetry}
            className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-60 hover:bg-brand-70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-50'
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (users.length === 0) {
    return (
      <div className='flex items-center justify-center p-12'>
        <div className='text-center space-y-4'>
          <div className='h-12 w-12 rounded-full bg-gray-10 flex items-center justify-center mx-auto'>
            <svg
              className='h-6 w-6 text-gray-60'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
              />
            </svg>
          </div>
          <div>
            <p className='text-lg font-medium text-gray-90'>
              No Users Available
            </p>
            <p className='text-sm text-gray-60 mt-1'>
              There are no users available for bulk permission updates.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <BulkPermissionUpdate
      users={users}
      roles={roles}
      onSuccess={handleSuccess}
      onCancel={handleCancel}
    />
  );
};

export default BulkPermissionUpdateSheet;
